import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { config } from '@/config';
import { prepareHeaders } from '@/configuration/tokenHandling/prepareHeaders';

// ── Response types ──

export interface VehicleStateConfig {
    name: string;
    initial: number;
    increment: number | null;
    decrement: number | null;
    limit: number | null;
}

export interface ScheduleSummary {
    index: number;
    cid: number;
    version: number;
    variants: string[];
    cron: string;
    description: string;
    overrides_count: number;
}

export interface DataMockFileMeta {
    name: string;
    path: string;
    type: 'independent' | 'dependent';
    category: string;
}

export interface VehicleConfig {
    vin: string;
    asset_id: string;
    asset_name: string;
    asset_type: string;
    device_type: string;
    device_serial_number: string;
    fuel_type: string;
    service_care_booking: string;
    description: string;
    state_config: VehicleStateConfig[];
    schedules: ScheduleSummary[];
    data_mock_files: DataMockFileMeta[];
}

export interface TemplateVariant {
    name: string;
    content: string;
}

export interface ScheduleTemplatesResponse {
    variants: TemplateVariant[];
    merged: string;
}

export interface ResolvedScheduleTemplateResponse {
    merged: string;
    overridden_json_paths: string[];
}

export interface AvailableMessage {
    cid: number;
    versions: number[];
}

export interface VehicleStateResponse {
    mileage: number | null;
    state: Record<string, number | string>;
}

// ── Arg types ──

interface ScheduleArgs {
    vin: string;
    index: number;
}

interface ProtobufArgs {
    cid: number;
    version: number;
}

interface DataMockArgs {
    vin: string;
    type: string;
    category: string;
}

// ── API matching DfpFrontendController ──

export const dfpFrontendApi = createApi({
    reducerPath: 'dfpFrontendApi',
    baseQuery: fetchBaseQuery({ baseUrl: config.backend.BACKEND_URI, prepareHeaders }),
    endpoints: (build) => ({
        checkPermission: build.query<void, void>({
            query: () => '/dfp-fe/permission-check',
        }),

        getVehicles: build.query<VehicleConfig[], void>({
            query: () => '/dfp-fe/vehicles',
        }),

        getScheduleTemplates: build.query<ScheduleTemplatesResponse, ScheduleArgs>({
            query: ({ vin, index }) => `/dfp-fe/vehicles/${vin}/schedules/${index}/templates`,
        }),

        getScheduleTemplatesResolved: build.query<ResolvedScheduleTemplateResponse, ScheduleArgs>({
            query: ({ vin, index }) => `/dfp-fe/vehicles/${vin}/schedules/${index}/templates/resolved`,
        }),

        getScheduleConfig: build.query<string, ScheduleArgs>({
            query: ({ vin, index }) => ({
                url: `/dfp-fe/vehicles/${vin}/schedules/${index}/config`,
                responseHandler: (response: Response) => response.text(),
            }),
        }),

        getProtobuf: build.query<string, ProtobufArgs>({
            query: ({ cid, version }) => ({
                url: `/dfp-fe/protobuf/${cid}/${version}`,
                responseHandler: (response: Response) => response.text(),
            }),
        }),

        getDataMockFile: build.query<string, DataMockArgs>({
            query: ({ vin, type, category }) => ({
                url: `/dfp-fe/vehicles/${vin}/data-mocks/${type}/${category}`,
                responseHandler: (response: Response) => response.text(),
            }),
        }),

        getAvailableMessages: build.query<AvailableMessage[], void>({
            query: () => '/dfp-fe/available-messages',
        }),

        getVehicleState: build.query<VehicleStateResponse, string>({
            query: (vin) => `/dfp-fe/vehicles/${vin}/state`,
        }),
    }),
});

export const {
    useCheckPermissionQuery,
    useGetVehiclesQuery,
    useGetScheduleTemplatesQuery,
    useLazyGetScheduleTemplatesQuery,
    useGetScheduleTemplatesResolvedQuery,
    useLazyGetScheduleTemplatesResolvedQuery,
    useGetScheduleConfigQuery,
    useGetProtobufQuery,
    useGetDataMockFileQuery,
    useGetAvailableMessagesQuery,
    useGetVehicleStateQuery,
} = dfpFrontendApi;
